import { FormControl, Grid, InputLabel, MenuItem, Select } from "@mui/material";
import { MprcButton, MprcFormSubHeader } from "components";
import { IconButtonDelete } from "components/iconButtons";
import { AutoCompleteDataModel } from "components/MprcAutoComplete";
import { MprcConfirmDialog } from "components/MprcConfirmDialog";
import { MprcContainer } from "components/MprcContainer";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { CustomFieldValueDto, ManagedUserQueryResponse, TagUsage, UserTagDto } from "generated/CoreApi_v1";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomFieldInput, getCustomFieldGridSize } from "views/foundation/customField/components/CustomFieldInput";
import { TagAutoComplete } from "views/foundation/tag/components/TagAutoComplete";
import { useEditUserContext } from "views/foundation/user/Home/EditUser/EditUser";

////////////////////////////////////////////////////////////////////////////

interface IValidationFields {
    customFields: { [key: string]: boolean; };
}

export interface IPostValidation {
    customFields: { [key: string]: string[] | undefined; };
}

export interface IUserAdditonalInfoBoxData {
    customFieldsAdditional: CustomFieldValueDto[] | null;
    customFieldsConcealed: CustomFieldValueDto[] | null;
    tags: UserTagDto[];
}
interface IProps {
    viewData: IUserAdditonalInfoBoxData;
    onFormValidation: (valid: boolean) => void;
}

////////////////////////////////////////////////////////////////////////////

///
const getDefaultValidate = (viewData: IUserAdditonalInfoBoxData): IValidationFields => {
    var obj = { customFields: {} } as IValidationFields;
    viewData.customFieldsAdditional
        ?.filter((x) => x.isRequired)
        .forEach((f) => (obj.customFields[f.fieldId] = f.fieldValue != undefined && f.fieldValue.toString().length > 0));

    return obj;
};

////////////////////////////////////////////////////////////////////////////

/**
 * Edição de Usuário - Informações Adicionais
 */
export const EditUserAdditionalInfoBox: React.FC<IProps> = (props) => {
    ///////////////////////////////////////////////////////////////////////////

    const { t } = useTranslation();
    const { viewData, onFormValidation } = props;
    const { isEditMode, viewErrors, isNewUser, readonly, onFormUpdate, onGoToSubForm, onDialog } = useEditUserContext();

    const [formValidation, setFormValidation] = useState<IValidationFields>(getDefaultValidate(viewData));
    const [postValidation, setPostValidation] = useState<IPostValidation>({ customFields: {} });
    const [concealedFields, setConcealedFields] = useState<CustomFieldValueDto[]>(viewData.customFieldsConcealed?.filter((x) => x.fieldValue) ?? []);

    const inputRef = useRef<any>(undefined);

    ////////////////////////////////////////////////////////////////////////////

    ///
    const handleUpdateRecord = (name: keyof ManagedUserQueryResponse, v: any) => {
        onFormUpdate({ [name]: v });
    };

    ///
    const handleUpdateCustomField = (field: CustomFieldValueDto, value: any, isValid: boolean) => {
        let newCustomFields = viewData.customFieldsAdditional?.map((item) => {
            if (item.fieldId === field.fieldId) item.fieldValue = value;
            return item;
        });

        handleUpdateRecord("customFieldsAdditional", newCustomFields);

        if (field.fieldValue != undefined || formValidation.customFields[field.fieldId] === false)
            setFormValidation({
                ...formValidation,
                customFields: { ...formValidation.customFields, [field.fieldId]: isValid }
            });
    };

    ///
    const handleUpdateConcealedField = (fieldId: string, value: any) => {
        let newConcealedFields = viewData.customFieldsConcealed?.map((item) => {
            if (item.fieldId === fieldId) {
                item.fieldValue = value;
            }
            return item;
        });

        handleUpdateRecord("customFieldsConcealed", newConcealedFields);
    };

    ///
    const handleAddConcealedField = (fieldId: string) => {
        var item = viewData.customFieldsConcealed?.find((x) => x.fieldId === fieldId) ?? undefined;
        if (item) setConcealedFields([...concealedFields, item]);

        //Focus no input adicionado e remove a referência após o uso para ser utilizado pelo próximo field adicionado
        setTimeout(() => {
            inputRef.current?.focus();
            inputRef.current = undefined;
        }, 50);
    };

    ///
    const handleRemoveConcealedField = (fieldId: string) => {
        onDialog(
            <MprcConfirmDialog
                open
                severity="danger"
                message={t("User.Edit.ConfirmDeleteConcealedFieldMessage")}
                onClose={(result) => {
                    if (result === true) {
                        setConcealedFields(concealedFields.filter((x) => x.fieldId != fieldId));
                        handleUpdateConcealedField(fieldId, null);
                    }

                    onDialog(null);
                }}
            />
        );
    };

    ///
    const handleChangeTag = (items: AutoCompleteDataModel[]) => {
        handleUpdateRecord("tags", items.map((x) => ({ tagId: x.key, tagName: x.label } as UserTagDto)));
    };

    ////////////////////////////////////////////////////////////////////////////

    //Inicializa os campos escamoteados com o que vem do viewdata quando concealedFields for vazio (ainda não inicializado)
    useEffect(() => {
        if (concealedFields.length === 0) {
            setConcealedFields(viewData.customFieldsConcealed?.filter((x) => x.fieldValue) ?? []);
        }
    }, [viewData.customFieldsConcealed]);

    //Ao atualizar o estado do formValidation
    useEffect(() => {
        if (Object.keys(formValidation.customFields).length === 0) {
            onFormValidation(true); //Se a tab não tiver campos custom obrigatórios, a validação retorna True
        } else {
            onFormValidation(Object.values(formValidation.customFields).filter((x) => x === false).length === 0);
        }
    }, [formValidation]);

    useEffect(() => {
        var fields = (viewData.customFieldsAdditional ?? []).concat(viewData.customFieldsConcealed ?? []);
        if (viewErrors != null && fields.length > 0) {

            const postValidationFields: { [key: string]: string[]; } = {};
            setFormValidation(() => {

                var customFields = fields.reduce(
                    (accumulator, currValue) => {
                        if (viewErrors.hasOwnProperty(`customFields.${currValue.fieldId}`)) {
                            accumulator[currValue.fieldId] = false;
                            postValidationFields[currValue.fieldId] = viewErrors[`customFields.${currValue.fieldId}`];
                        }
                        return accumulator;
                    }, {} as { [key: string]: boolean; }) ?? {};

                return { customFields: customFields };
            }
            );

            setPostValidation(({ customFields: postValidationFields }));
        }
    }, [viewErrors]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcContainer margin="large">
                <MprcFormSubHeader
                    title={t("SysObjects.Tags", { ns: "shared" })}
                    subtitle={t("User.Edit.TagHint")}
                />
                <TagAutoComplete
                    placeholder={t("SysObjects.Tags", { ns: "shared" })}
                    usage={TagUsage.User}
                    disabled={readonly}
                    selectedItems={
                        viewData.tags
                            ? viewData.tags.map((x) => ({ key: x.tagId, label: x.tagName, canDelete: true } as AutoCompleteDataModel))
                            : []
                    }
                    onChange={handleChangeTag}
                    canCreate
                    onError={(error) => MprcRequestSnackbarErrorReport(error)}
                />
            </MprcContainer>

            <MprcContainer margin="large">
                <MprcFormSubHeader
                    margin="dense"
                    title={t("User.Edit.RecordFilesSubtitle")}
                    subtitle={t("User.Edit.EditSubFormHint")}
                />

                <MprcButton
                    title={t("User.Edit.RecordFilesButtonTooltip")}
                    text={t("User.Edit.RecordFilesButtonText")}
                    variant="contained"
                    rounded
                    disabled={isEditMode}
                    onClick={() => onGoToSubForm("files")}
                />
            </MprcContainer>

            {
                ((viewData.customFieldsAdditional ?? []).length > 0 || (viewData.customFieldsConcealed ?? []).length > 0 || concealedFields.length > 0) && (
                    <MprcContainer margin="large">
                        <MprcFormSubHeader
                            title={t("User.Edit.OtherInfoSubtitle")}
                        />
                        {
                            (viewData.customFieldsAdditional ?? []).length > 0 && (
                                <Grid container rowSpacing={1} columnSpacing={4}>
                                    {
                                        viewData.customFieldsAdditional!.map((item, i) => (
                                            <Grid key={i} size={{ xs: getCustomFieldGridSize(item.showWidth) }}>
                                                <CustomFieldInput
                                                    field={item}
                                                    useDefaultValue={isNewUser}
                                                    disabled={readonly}
                                                    onChange={(value, formattedValue, isValid) => handleUpdateCustomField(item, value, isValid)}
                                                    error={postValidation.customFields[item.fieldId]}
                                                    validateOnFirstMount={true}
                                                />
                                            </Grid>
                                        ))}
                                </Grid>
                            )
                        }

                        {
                            (viewData.customFieldsConcealed ?? []).length > 0 && (
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>{t("User.Edit.SelectCustomFieldLabel")}</InputLabel>
                                    <Select disabled={readonly} defaultValue="" value="" onChange={(e) => handleAddConcealedField(e.target.value as string)}>
                                        {
                                            viewData.customFieldsConcealed
                                                ?.filter((item) => concealedFields.findIndex((x) => x.fieldId === item.fieldId) === -1)
                                                .map((item) => (
                                                    <MenuItem key={item.fieldId} value={item.fieldId}>
                                                        {item.label}
                                                    </MenuItem>
                                                ))}
                                    </Select>
                                </FormControl>
                            )
                        }

                        {
                            concealedFields && (
                                <Grid container rowSpacing={1}>
                                    {concealedFields.map((item, i) => (
                                        <Grid key={i} container justifyContent="space-between" alignItems="end" wrap="nowrap">
                                            <Grid size={{ xs: getCustomFieldGridSize(item.showWidth) }}>
                                                <CustomFieldInput
                                                    margin="dense"
                                                    ref={inputRef}
                                                    field={item}
                                                    useDefaultValue={isNewUser}
                                                    disabled={readonly}
                                                    onChange={(value) => handleUpdateConcealedField(item.fieldId, value)}
                                                    error={postValidation.customFields[item.fieldId] || !formValidation.customFields[item.fieldId]}
                                                    validateOnFirstMount={true}
                                                />
                                            </Grid>
                                            <Grid>
                                                <IconButtonDelete size="small" onClick={() => handleRemoveConcealedField(item.fieldId)} />
                                            </Grid>
                                        </Grid>
                                    ))}
                                </Grid>
                            )
                        }
                    </MprcContainer>
                )
            }
        </>
    );
};